// externals
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { uniq } from 'lodash';

// libraries
import * as offerInfoSelectors from '@makemydeal/dr-dash-store/selectors/offerInfoSelectors';
import * as pendingDealerReviewSelectors from '@makemydeal/dr-dash-store/selectors/pendingDealerReviewSelectors';
import { isSalesCheckInName } from '@makemydeal/dr-shared-types';

// utils
import { buildSciUpdateSummary, parseSciVersionComment } from './sciVersionComment';

export type PendingSciUpdate = {
    isPendingSocketUpdate: boolean;
    showChangedFields: boolean;
    isSalesCheckInAttributed: boolean;
    labels: string[];
    sender: string;
    freeTextComment: string;
    targetVersion: string;
    reset: () => void;
};

export const usePendingSciUpdate = (): PendingSciUpdate => {
    const [accumulatedDescriptors, setAccumulatedDescriptors] = useState([] as string[]);

    const socketEventNotification = useSelector(offerInfoSelectors.getSocketEventNotification);
    const socketComment = useSelector(offerInfoSelectors.getSocketEventComment);
    const socketVersion = useSelector(offerInfoSelectors.getSocketEventDealVersion);
    const socketVersionApplicationName = useSelector(pendingDealerReviewSelectors.getSocketVersionApplicationName);
    const pendingReview = useSelector(offerInfoSelectors.getPendingReview);
    const pendingReviewResolution = useSelector(offerInfoSelectors.getPendingReviewResolution);

    useEffect(() => {
        if (socketEventNotification) {
            const descriptor = parseSciVersionComment(socketComment)?.descriptor;
            setAccumulatedDescriptors((prev) => (descriptor ? uniq(prev.concat(descriptor)) : prev));
        }
    }, [socketEventNotification]);

    // A resolved context is left in state rather than cleared — see PendingReviewRetentionHost, which
    // guards the same way. Without this every later Sales Check-In version re-renders the resolved
    // banner and its Apply re-applies the version already applied.
    const activePendingReview = pendingReviewResolution === undefined ? pendingReview : undefined;
    // Not isPendingDealerReview: that reads the loaded deal's status, and in the primary live scenario
    // the dealer is on their own non-pending version when SCI publishes.
    const heldReview = activePendingReview?.trigger === 'socket' ? activePendingReview : undefined;

    const summary = buildSciUpdateSummary({
        changedFields: heldReview?.changedFields,
        versionComment: socketComment,
        accumulatedDescriptors
    });

    return {
        isPendingSocketUpdate: Boolean(heldReview),
        showChangedFields: summary.labels.length > 0,
        isSalesCheckInAttributed: isSalesCheckInName(socketVersionApplicationName) || Boolean(heldReview?.marker),
        labels: summary.labels,
        sender: summary.sender,
        freeTextComment: summary.freeTextComment,
        targetVersion: activePendingReview?.targetDealXgVersion || socketVersion,
        reset: () => setAccumulatedDescriptors([])
    };
};
